import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, LandmarkIcon, FileTextIcon, UsersIcon, VoteIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Council Meetings & Records
{
  id: 'cm1',
  title: 'Council Meeting Agenda Access',
  description: 'Request or access municipal council meeting agendas and schedules',
  category: 'Council Meetings & Records',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'cm2',
  title: 'Meeting Minutes Request',
  description: 'Access official minutes and records from past council meetings',
  category: 'Council Meetings & Records',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'cm3',
  title: 'Freedom of Information Request',
  description: 'Submit formal request for public records and municipal documents',
  category: 'Council Meetings & Records',
  estimatedTime: '10-15 business days',
  fee: '$25.00 processing fee'
},
// Public Participation
{
  id: 'pp1',
  title: 'Online Public Consultation',
  description: 'Participate in current public consultations and surveys',
  category: 'Public Participation',
  estimatedTime: 'Immediate',
  fee: 'No fee'
}, {
  id: 'pp2',
  title: 'Local Board Application',
  description: 'Apply for appointment to local boards and committees',
  category: 'Public Participation',
  estimatedTime: '15-20 business days',
  fee: 'No fee'
}, {
  id: 'pp3',
  title: 'Service Feedback Submission',
  description: 'Submit feedback or complaints about municipal services',
  category: 'Public Participation',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'pp4',
  title: 'Committee Membership Registration',
  description: 'Register interest in joining municipal committees',
  category: 'Public Participation',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
},
// Elections & Voting
{
  id: 'ev1',
  title: 'Voter Registration Update',
  description: 'Update your voter registration information',
  category: 'Elections & Voting',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'ev2',
  title: 'Election Information Request',
  description: 'Access information about upcoming local elections',
  category: 'Elections & Voting',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'ev3',
  title: 'Polling Station Lookup',
  description: 'Find your designated polling station for upcoming elections',
  category: 'Elections & Voting',
  estimatedTime: 'Immediate',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Council Meetings & Records':
      return <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-3540" />;
    case 'Public Participation':
      return <UsersIcon className="h-6 w-6 text-green-600" data-id="element-3541" />;
    case 'Elections & Voting':
      return <VoteIcon className="h-6 w-6 text-purple-600" data-id="element-3542" />;
    default:
      return <LandmarkIcon className="h-6 w-6 text-gray-600" data-id="element-3543" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3544">
      <div className="p-5 flex flex-col h-full" data-id="element-3545">
        <div className="flex-1" data-id="element-3546">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3547">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3548">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3549">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3550">
            <div className="flex items-center" data-id="element-3551">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3552">
                ⏱
              </span>
              <span data-id="element-3553">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3554">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3555">
                💰
              </span>
              <span data-id="element-3556">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3557">
            Access Service
            <span className="ml-1" data-id="element-3558">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3559">
      <div className="flex items-center mb-4" data-id="element-3560">
        <CategoryIcon category={category} data-id="element-3561" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3562">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3563">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3564" />)}
      </div>
    </div>;
};
export const CivicEngagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3565">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3566">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3567">
          <a href="/" className="hover:text-gray-700" data-id="element-3568">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3569" />
          <span className="text-gray-900" data-id="element-3570">Civic Engagement</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3571">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3572">
            Civic Engagement
          </h1>
          <p className="text-gray-600" data-id="element-3573">
            Participate in local governance through council meetings, public
            consultations, and elections
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3574">
          <div className="max-w-md relative" data-id="element-3575">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3576">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3577" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3578" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3579" />)}
      </div>
    </div>;
};